import { FormInput, FormSelect, useTranslation as useUiTranslation } from '@systemeio/ui'
import { UnprocessableContentError } from '@systemeio/util'
import { usePaymentSettings } from 'modules/profile/payment/hooks/use-payment-settings'
import { STORES_API } from 'modules/stores/api/stores-api'
import {
  storeDefaultCreateValues,
  storeDefaultErrors,
} from 'modules/stores/constants/store-default-values'
import { StoresLoco } from 'modules/stores/constants/stores-loco'
import { useStores } from 'modules/stores/hooks/use-stores'
import { useStoresApi } from 'modules/stores/hooks/use-stores-api'
import { StoreTabEnum } from 'modules/stores/store/enum/store-tab-enum'
import { useStoreQuery } from 'modules/stores/store/hooks/use-store-query'
import { getStoreUpdateData } from 'modules/stores/store/utils/get-store-update-data'
import { StoreCreateInterface, StoreInterface } from 'modules/stores/types/stores-interface'
import { NextPage } from 'next'
import { useRouter } from 'next/router'
import React, { useEffect, useState } from 'react'
import { CopyToClipboardButton } from 'shared/components/copy-to-clipboard-button'
import FormUrlPathInput from 'shared/components/form-url-path-input'
import LinkWithoutPrefetch from 'shared/components/link-without-prefetch'
import ListingLayout from 'shared/components/listing-layout'
import { StatusCell } from 'shared/components/table/cells/status-cell'
import { Table } from 'shared/components/table/table'
import { ColumnDefinitionInterface } from 'shared/components/table/types/column-interface'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import { useMutationUpdate } from 'shared/hooks/use-mutation-update'
import useUser from 'shared/hooks/use-user'
import useUserDomains from 'shared/hooks/use-user-domains'
import { dateTimeFormat } from 'shared/utils/date-time-format'
import { getBaseServerSideProps } from 'shared/utils/get-base-server-side-props'
import { getLocaleSelectOptions } from 'shared/utils/locale/get-locale-select-options'
import { handle422Error } from 'shared/utils/parse-422-errors'
import { generatePathFromName } from 'shared/utils/path-utils'

const Stores: NextPage = () => {
  const { t } = useLocoTranslation()
  const { t: tUi } = useUiTranslation()
  const { push } = useRouter()
  const { BASE_HREF } = useStoreQuery()
  const { user, isWsAssistant } = useUser()
  const { userDomainsSelect, userDomains, userDefaultDomain } = useUserDomains()
  const { stores, mutate } = useStores()
  const { createStore, updateStore, deleteStore } = useStoresApi()
  const [tempState, setTempState] = useState<StoreCreateInterface>(storeDefaultCreateValues)
  const [errors, setErrors] = useState(storeDefaultErrors)

  const { paymentSettingsData } = usePaymentSettings(!isWsAssistant)

  const mutationUpdate = useMutationUpdate()

  useEffect(() => {
    if (userDefaultDomain) {
      setTempState(prev => ({
        ...prev,
        domainId: userDefaultDomain.id,
      }))
    }
    if (paymentSettingsData) {
      setTempState(prev => ({
        ...prev,
        currency: paymentSettingsData.defaultCurrency,
      }))
    }
  }, [userDefaultDomain, paymentSettingsData])

  useEffect(() => {
    if (user?.dashboardLocale) {
      setTempState(prev => ({
        ...prev,
        locale: user.dashboardLocale,
      }))
    }
  }, [user?.dashboardLocale])

  const columns: ColumnDefinitionInterface<StoreInterface>[] = [
    {
      key: 'name',
      header: t('global.name'),
      onRender: store => (
        <div className="truncate">
          <LinkWithoutPrefetch className="primary-link w-full" href={`/stores/${store.id}`}>
            {store.name}
          </LinkWithoutPrefetch>
        </div>
      ),
      widthPercent: 30,
    },
    {
      key: 'path',
      header: t('dashboard.stores.home_page_url'),
      onRender: store => {
        if (!userDomains)
          return (
            <div className={'bg-skeleton-primary animate-pulse rounded-md px-10 text-transparent'}>
              .
            </div>
          )
        const domainName = userDomains?.find(el => el.id === store.domainId)?.name
        const homePageUrl = domainName
          ? `${process.env.NEXT_PUBLIC_SIO_HOST_SCHEME}://${domainName}/${store.path}`
          : ''
        return (
          <div className="flex w-full gap-3">
            <div className="truncate">{homePageUrl}</div>
            <CopyToClipboardButton text={homePageUrl} />
          </div>
        )
      },
      widthPercent: 30,
    },
    {
      key: 'active',
      header: t('global.status'),
      widthPercent: 10,
      onRender: store => <StatusCell isActive={store.active} />,
    },
    {
      key: 'createdAt',
      header: t('global.created_on'),
      widthPercent: 30,
      onRender: store => (
        <span>
          {dateTimeFormat(store.createdAt, {
            timeFormat: user?.timeFormat,
            dateFormat: user?.dateFormat,
            timeZone: user?.timezone,
          })}
        </span>
      ),
    },
  ]
  const onCreate = async (store: StoreCreateInterface) => {
    try {
      setErrors(storeDefaultErrors)
      const responseData = await createStore(store)
      await mutate(
        data => data && { ...data, total: data.total + 1, items: [responseData, ...data.items] },
        false,
      )
      await mutationUpdate<StoreInterface>(`${STORES_API}/${responseData.id}`, () => responseData)
      await push(`${BASE_HREF}/${responseData.id}`)
    } catch (e) {
      handle422Error(e, setErrors, {
        setCommonError: common => setErrors(prev => ({ ...prev, common })),
      })
      throw e
    }
  }

  const onRemove = async (store: StoreInterface) => {
    await deleteStore(store.id)
    await mutate(
      data =>
        data && {
          ...data,
          total: data.total - 1,
          items: data.items.filter(el => el.id !== store.id),
        },
      false,
    )
  }

  const onEdit = async (store: StoreInterface) => {
    try {
      const responseData = await updateStore(store.id, getStoreUpdateData(store))
      await mutate(
        prev =>
          prev && {
            ...prev,
            items: prev.items.map(item => (item.id === store.id ? responseData : item)),
          },
        false,
      )
    } catch (e) {
      handle422Error(e, setErrors)
      throw e
    }
  }

  const onChangeStatus = async (store: StoreInterface) =>
    await onEdit({ ...store, active: !store.active })

  const onModalClose = () => setErrors(storeDefaultErrors)

  return (
    <ListingLayout
      data={stores && { items: stores.items, hasMore: false }}
      title={t('dashboard.stores.title')}
      type={'compact'}
      counter={stores && { count: stores.total }}
      createData={{
        defaultValues: tempState,
        onCreate: data => onCreate({ ...tempState, ...data }),
        onAfterLeave: onModalClose,
        caption: t('dashboard.store.title'),
        commonError: errors.common,
        disableErrorToast: e => e instanceof UnprocessableContentError,
        disabled: !stores || !userDomainsSelect,
        requiredFields: ['name', 'domainId', 'locale'],
        creatable: [
          [
            {
              field: 'name',
              onRender: (value, onChange) => (
                <FormInput
                  required
                  key="store-name-create"
                  label={t('global.name')}
                  value={value}
                  onChange={e => onChange(e.target.value)}
                  error={errors.name}
                />
              ),
            },
            {
              field: 'domainId',
              onRender: (value, onChange) => (
                <FormSelect
                  required
                  withoutCloseIcon
                  key="store-domain-create"
                  onChange={value => onChange(value)}
                  value={value}
                  label={t('dashboard.communities.domain_name')}
                  data={userDomainsSelect}
                  error={errors.domainId}
                />
              ),
            },
          ],
          {
            field: 'path',
            onRender: (value, onChange, data) => (
              <FormUrlPathInput
                key="store-path-create"
                error={errors.path}
                value={value}
                onChange={e => onChange(e.target.value)}
                onFocus={e => {
                  if (!e.target.value) {
                    onChange(generatePathFromName(data.name))
                  }
                }}
                label={t('global.url_path')}
                domainName={userDomainsSelect?.find(el => el.id === data.domainId)?.caption}
              />
            ),
          },
          {
            field: 'locale',
            onRender: (value, onChange) => (
              <FormSelect
                key="store-locale-create"
                required
                withoutCloseIcon
                onChange={value => onChange(value)}
                value={value}
                label={t('global.language')}
                data={getLocaleSelectOptions(tUi)}
                placeholder={t('global.language')}
                error={errors.locale}
              />
            ),
          },
        ],
      }}
    >
      <Table
        caption={t('dashboard.store.title')}
        data={stores && { items: stores.items, hasMore: false }}
        columns={columns}
        actions={{
          activate: { customStatusField: 'active', onChange: onChangeStatus },
          custom: [
            {
              caption: t('dashboard.actions.title.settings'),
              name: 'edit',
              onActionClick: instance =>
                push({
                  pathname: `${BASE_HREF}/${instance.id}`,
                  query: { tab: StoreTabEnum.storeSettings },
                }),
            },
          ],
          remove: {
            onChange: onRemove,
          },
        }}
      />
    </ListingLayout>
  )
}

export const getServerSideProps = getBaseServerSideProps(StoresLoco, 'header.top_menu.stores')

export default Stores
